'use client';

import type { FeatureGroup, Tier } from './types';

interface Props {
  tiers: Tier[];
  groups: FeatureGroup[];
}

function Cell({ value }: { value: string | boolean }) {
  if (value === true) {
    return (
      <svg
        className="mx-auto h-5 w-5 text-emerald-500"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-label="Included"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (value === false) {
    return <span className="block text-center text-slate-300">—</span>;
  }
  return <span className="block text-center text-sm font-semibold text-slate-900">{value}</span>;
}

/**
 * Comparison table with grouped feature rows and dynamic tier columns.
 * Sticky header so tier names stay visible while scrolling long groups.
 */
export default function FeatureComparisonTable({ tiers, groups }: Props) {
  const columns = { gridTemplateColumns: `minmax(180px, 1.8fr) repeat(${tiers.length}, minmax(120px, 1fr))` };

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      {/* Header row */}
      <div className="sticky top-0 z-10 min-w-[760px] border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="grid" style={columns}>
          <div className="px-6 py-4 text-sm font-semibold text-slate-900">
            Compare all features
          </div>
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`px-3 py-4 text-center ${
                tier.isPopular
                  ? 'bg-gradient-to-b from-indigo-50 to-white'
                  : ''
              }`}
            >
              <div className="text-sm font-semibold text-slate-900">
                {tier.name}
              </div>
              {tier.isPopular && (
                <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">
                  Best Value
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Grouped rows */}
      {groups.map((group) => (
        <div key={group.title} className="min-w-[760px]">
          <div className="grid border-b border-slate-100 bg-slate-50" style={columns}>
            <div className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {group.title}
            </div>
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className={tier.isPopular ? 'bg-indigo-50/50' : ''}
              />
            ))}
          </div>

          {group.rows.map((row, idx) => (
            <div
              key={row.label}
              style={columns}
              className={`grid ${
                idx !== group.rows.length - 1 ? 'border-b border-slate-100' : ''
              } ${row.highlight ? 'bg-amber-50/30' : ''}`}
            >
              <div className="flex items-center px-6 py-3.5 text-sm text-slate-700">
                {row.label}
                {row.highlight && (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                    Key
                  </span>
                )}
              </div>
              {tiers.map((tier) => (
                <div
                  key={tier.id}
                  className={`flex items-center justify-center px-3 py-3.5 ${
                    tier.isPopular ? 'bg-indigo-50/50' : ''
                  }`}
                >
                  <Cell value={row.values[tier.id]} />
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
